import defaults from '../api/defaults';
import {
    ActionType, ExcelGenerationIndicatorDepthRule, ExcelGenerationValueWriteRule, NetworkActionPrefix, RequestStatus,
    SheetConfigurationForecastDownloadFromSheetRule, SheetConfigurationForecastUploadToSheetRule, SheetForecastFileStatus, SheetForecastFileType, SheetForecastInputMethod,
    SheetGoogleTemplateCopiedRangeValueType
} from '../utils/constants';
import {
    combineReducers, networkActionReducer, networkEntityAddReducer, networkEntityDeleteReducer, networkEntityEditReducer, networkEntityLoadReducer,
    networkObjectLoadReducer, networkObjectTrackChangesReducer, pipeReducers, synchronizableEnumReducer
} from '../utils/reducerFactory';
import { deepAssign } from '../utils';

function index(state, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

const keyVariables = {
        googleOAuthUserIndex: (state, action) => index(state, action.googleOAuthUserID),
        sheetConfigurationIndex: (state, action) => index(state, action.sheetConfigurationID),
        sheetExcelConfigurationIndex: (state, action) => index(state, action.sheetExcelConfigurationID),
        sheetForecastIndex: (state, action) => index(state, action.sheetForecastID),
        sheetForecastFileIndex: (state, action) => index(state, action.sheetForecastFileID),
        sheetForecastRelatedSheetIndex: (state, action) => index(state, action.sheetForecastRelatedSheetID),
        sheetForecastNotificationUserIndex: (state, action) => index(state, action.sheetForecastNotificationUserID),
        sheetGoogleConfigurationIndex: (state, action) => index(state, action.sheetGoogleConfigurationID)
    },

    sheets = combineReducers({
        aggregationPreviewData: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.AGGREGATE_SHEET_FORECAST_FILE_DATA_PREFIX, defaults.NetworkArray),
            (state, action) => {
                if (action.type === ActionType.AGGREGATE_SHEET_FORECAST_FILE_DATA_SUCCESS && !action.preview) {
                    return defaults.NetworkArray;
                }

                return state;
            }
        ),
        excelGenerationIndicatorDepthRules:
        synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_EXCEL_GENERATION_INDICATOR_RULES_PREFIX, defaults.NetworkArray, ExcelGenerationIndicatorDepthRule, 'SheetExcelGenerationIndicatorDepthRule'),
        excelGenerationValueWriteRules:
        synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_EXCEL_GENERATION_VALUE_WRITE_RULES_PREFIX, defaults.NetworkArray, ExcelGenerationValueWriteRule, 'SheetExcelGenerationValueWriteRule'),
        excelShareURL: pipeReducers(
            (state, action) => {
                const currentState = state || deepAssign(defaults.NetworkObject, {
                    Data: false,
                    ShareURL: ''
                });

                switch (action.type) {
                    case ActionType.TEST_EXCEL_SHARE_URL_PENDING:
                        return deepAssign(currentState, { Data: false, ShareURL: action.shareURL });

                    case ActionType.TEST_EXCEL_SHARE_URL_SUCCESS:
                        return deepAssign(currentState, { Data: action.response });

                    default:
                        return currentState;
                }
            },
            networkActionReducer(NetworkActionPrefix.TEST_EXCEL_SHARE_URL_PREFIX)
        ),
        generatedSheet: pipeReducers(
            networkEntityLoadReducer(NetworkActionPrefix.CREATE_GOOGLE_SPREADSHEET_FROM_INDICATORS_PREFIX, defaults.SheetGoogleConfiguration),
            (state, action) => {
                switch (action.type) {
                    case ActionType.DROP_GENERATED_SHEET:
                        return defaults.SheetGoogleConfiguration;

                    default:
                        return state;
                }
            }
        ),
        googleOAuthUsers: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_GOOGLE_OAUTH_USERS_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@googleOAuthUserIndex': networkEntityDeleteReducer(NetworkActionPrefix.DELETE_GOOGLE_OAUTH_USER_PREFIX)
                }, keyVariables)
            })
        ),
        sheetForecastInputMethods: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_SHEET_FORECAST_INPUT_METHODS_PREFIX, defaults.NetworkArray, SheetForecastInputMethod, 'SheetForecastInputMethod'),
        sheetForecastFileStatuses: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_SHEET_FORECAST_FILE_STATUSES_PREFIX, defaults.NetworkArray, SheetForecastFileStatus, 'SheetForecastFileStatus'),
        sheetForecastFileTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_SHEET_FORECAST_FILE_TYPES_PREFIX, defaults.NetworkArray, SheetForecastFileType, 'SheetForecastFileType'),
        sheetForecasts: pipeReducers(
            networkObjectTrackChangesReducer(['SheetForecast', 'SheetForecastFile']),
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_SHEET_FORECASTS_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@sheetForecastIndex': pipeReducers(
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_SHEET_FORECAST_PREFIX),
                        networkEntityAddReducer(NetworkActionPrefix.ADD_SHEET_FORECAST_PREFIX, null, (state, action) => action.sheetForecast),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_SHEET_FORECAST_PREFIX, null, (state, action) => action.sheetForecast),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_SHEET_FORECAST_PREFIX),
                        combineReducers({
                            Files: pipeReducers(
                                combineReducers({
                                    '@sheetForecastFileIndex': pipeReducers(
                                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_SHEET_FORECAST_FILE_PREFIX),
                                        networkEntityAddReducer(NetworkActionPrefix.ADD_SHEET_FORECAST_FILE_PREFIX, null, (state, action) => action.sheetForecastFile),
                                        networkEntityEditReducer(NetworkActionPrefix.EDIT_SHEET_FORECAST_FILE_PREFIX, null, (state, action) => action.sheetForecastFile),
                                        networkActionReducer(NetworkActionPrefix.SHEET_FORECAST_FILE_SEND_REMINDER_PREFIX),
                                        networkActionReducer(NetworkActionPrefix.TOUCH_SHEET_FORECAST_FILE_PREFIX),
                                        combineReducers({
                                            Custom: combineReducers({
                                                import: networkActionReducer(NetworkActionPrefix.IMPORT_FORECAST_SPREADSHEET_PREFIX),
                                                update: networkActionReducer(NetworkActionPrefix.GENERATE_FORECAST_SHEET_CONTENTS_PREFIX)
                                            }, keyVariables)
                                        }),
                                        (state, action) => {
                                            switch (action.type) {
                                                case action.withNotification ? ActionType.ADD_SHEET_FORECAST_FILE_SUCCESS : null:
                                                case action.withNotification ? ActionType.EDIT_SHEET_FORECAST_FILE_SUCCESS : null:
                                                case ActionType.SHEET_FORECAST_FILE_SEND_REMINDER_SUCCESS:
                                                    return deepAssign(state, {
                                                        StatusID: !action.hasOwnProperty('sheetForecastFile') ? action.statusID : action.sheetForecastFile.StatusID,
                                                        ManagerNote: !action.hasOwnProperty('sheetForecastFile') ? action.managerNote : action.sheetForecastFile.ManagerNote,
                                                        LastReminderSentDate: new Date()
                                                    });

                                                case ActionType.SET_SHEET_FORECAST_FILE_USER_ACCESSES_SUCCESS:
                                                    return deepAssign(state, {
                                                        UserAccesses: action.response
                                                    });

                                                case ActionType.TOUCH_SHEET_FORECAST_FILE_SUCCESS:
                                                    return deepAssign(state, {
                                                        StatusID: !action.hasOwnProperty('sheetForecastFile') ? action.statusID : action.sheetForecastFile.StatusID,
                                                        ResponsibleUserNote: !action.hasOwnProperty('sheetForecastFile') ? action.responsibleUserNote : action.sheetForecastFile.ResponsibleUserNote,
                                                        LastUserUpdate: new Date()
                                                    });
                                                default:
                                                    return state;
                                            }
                                        },
                                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_SHEET_FORECAST_FILE_PREFIX)
                                    )
                                }, keyVariables),
                                (state, action) => action.type === ActionType.DELETE_SHEET_FORECAST_FILE_SUCCESS ?
                                    deepAssign(state, state.filter(file => file.FatherID !== action.sheetForecastFileID)) :
                                    state
                            ),
                            RelatedSheets: combineReducers({
                                '@sheetForecastRelatedSheetIndex': pipeReducers(
                                    networkEntityAddReducer(NetworkActionPrefix.ADD_SHEET_FORECAST_RELATED_SHEET_PREFIX, null, (state, action) => action.sheetForecastRelatedSheet),
                                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_SHEET_FORECAST_RELATED_SHEET_PREFIX)
                                )
                            }, keyVariables)
                        })
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.ADD_SHEET_FORECAST_SUCCESS:
                    case ActionType.EDIT_SHEET_FORECAST_SUCCESS:
                        return deepAssign(state, { Data: state.Data.copy().sortBy(({ Name }) => Name) });

                    case ActionType.ADD_ENTITY_CHANGE:
                        if (action.change.EntityType === defaults.SheetForecastFile.EntityType) {
                            const forecast = state.Data.find(({ Files }) => Files.getByID(action.change.EntityID));

                            if (forecast?.Files.getByID(action.change.EntityID)) {
                                const optionalDetails = action.change.OptionalDetails,
                                    details = {};

                                Object.keys(optionalDetails).forEach((key) => {
                                    if (!optionalDetails.hasOwnProperty(key)) {
                                        return;
                                    }

                                    switch (key) {
                                        case 'LastErrorDate':
                                            details[key] = optionalDetails[key] ? new Date(optionalDetails[key]) : null;
                                            break;

                                        case 'StatusID':
                                            details[key] = parseInt(optionalDetails[key]);
                                            break;

                                        default:
                                            details[key] = optionalDetails[key];
                                    }
                                });


                                return deepAssign(state, {
                                    Data: {
                                        [state.Data.getIndexByID(forecast.ID)]: {
                                            Files: {
                                                [forecast.Files.getIndexByID(action.change.EntityID)]: {
                                                    ...details,
                                                    LastUserUpdate: new Date(action.change.TimeStamp)
                                                }
                                            }
                                        }
                                    }
                                });
                            }
                        }

                        return state;

                    default:
                        return state;
                }
            }
        ),
        sheetGoogleTemplateCopiedRangeValueTypes:
        synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_SHEET_GOOGLE_TEMPLATE_COPIED_RANGE_VALUE_TYPE_PREFIX, defaults.NetworkArray, SheetGoogleTemplateCopiedRangeValueType, 'SheetGoogleTemplateCopiedRangeValueType'),
        spreadsheet: pipeReducers(
            networkEntityLoadReducer(NetworkActionPrefix.LOAD_EXCEL_SPREADSHEET_PREFIX, defaults.ExternalSourceLightSpreadsheet),
            networkEntityLoadReducer(NetworkActionPrefix.LOAD_GOOGLE_SPREADSHEET_PREFIX, defaults.ExternalSourceLightSpreadsheet),
            (state, action) => {
                switch (action.type) {
                    case ActionType.DROP_SPREADSHEET:
                        return deepAssign(state, { ...defaults.ExternalSourceLightSpreadsheet, Custom: state.Custom });

                    default:
                        return state;
                }
            }
        ),
        spreadsheetImportedResults: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.IMPORT_AND_PROCESS_GOOGLE_SHEETS_DATA_PREFIX, defaults.NetworkArrayOf(defaults.PerformanceIndicatorDataReport)),
            networkObjectLoadReducer(NetworkActionPrefix.IMPORT_AND_PROCESS_EXCEL_FILE_PREFIX, null),
            networkObjectLoadReducer(NetworkActionPrefix.REPROCESS_STORED_EXCEL_FILE_PREFIX, null),
            (state, action) => {
                switch (action.type) {
                    case ActionType.DROP_SPREADSHEET_IMPORTED_RESULTS:
                        return deepAssign(state, defaults.NetworkArrayOf(defaults.PerformanceIndicatorDataReport));

                    default:
                        return state;
                }
            }
        ),
        sheetConfigurations: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_SHEET_CONFIGURATIONS_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@sheetConfigurationIndex': pipeReducers(
                        networkEntityAddReducer(NetworkActionPrefix.ADD_SHEET_CONFIGURATION_PREFIX, null, (state, action) => action.sheetConfiguration),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_SHEET_CONFIGURATION_PREFIX, null, (state, action) => action.sheetConfiguration),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_SHEET_CONFIGURATION_PREFIX)
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.CLONE_SHEET_GOOGLE_CONFIGURATION_SUCCESS:
                    case ActionType.ADD_SHEET_FORECAST_FILE_SUCCESS:
                    case ActionType.DELETE_SHEET_FORECAST_FILE_SUCCESS:
                        return defaults.NetworkArray;

                    case ActionType.COPY_RANGES_FROM_TEMPLATE_GOOGLE_SHEET_SUCCESS:
                        return action.settings.Ranges.some(({ ValueTypeID }) => ValueTypeID === SheetGoogleTemplateCopiedRangeValueType.CONFIGURATION) ?
                            defaults.NetworkArray :
                            state;

                    case ActionType.CREATE_GOOGLE_SPREADSHEET_FROM_INDICATORS_SUCCESS:
                    case ActionType.CREATE_GOOGLE_SPREADSHEET_FROM_FINANCIAL_PROFIT_AND_LOSS_INDICATORS_SUCCESS:
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });

                    case ActionType.DELETE_PERFORMANCE_INDICATOR_SUCCESS:
                        return deepAssign(state, {
                            Data: state.Data.map(configuration => configuration.PerformanceIndicatorID === action.indicatorID ? { ...configuration, PerformanceIndicatorID: null } : configuration)
                        });

                    case ActionType.DELETE_SHEET_GOOGLE_CONFIGURATION_SUCCESS:
                        return deepAssign(state, { Data: state.Data.filter(configuration => configuration.SheetGoogleID !== action.sheetGoogleConfigurationID) });

                    case ActionType.EDIT_ALL_SHEET_CONFIGURATIONS:
                    case ActionType.EDIT_ALL_SHEET_CONFIGURATIONS_PENDING:
                    case ActionType.EDIT_ALL_SHEET_CONFIGURATIONS_SUCCESS:
                    case ActionType.EDIT_ALL_SHEET_CONFIGURATIONS_FAIL:

                        return deepAssign(state, {
                            Data: state.Data.map((configuration) => {
                                const changes = action.sheetConfigurations.getByID(configuration.ID);

                                if (!changes) {
                                    return configuration;
                                }

                                return networkActionReducer(NetworkActionPrefix.EDIT_ALL_SHEET_CONFIGURATIONS_PREFIX)(changes, action);
                            })
                        });

                    case ActionType.ADD_SHEET_FORECAST_SUCCESS:
                    case ActionType.EDIT_SHEET_FORECAST_SUCCESS:
                        if (action.linkToTemplate) {
                            return defaults.NetworkArray;
                        }

                        return state;

                    default:
                        return state;
                }
            }
        ),
        sheetConfigurationForecastDownloadRules: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_SHEET_CONFIGURATION_FORECAST_DOWNLOAD_RULES_PREFIX, defaults.NetworkArray, SheetConfigurationForecastDownloadFromSheetRule, 'SheetConfigurationForecastDownloadFromSheetRule'),
        sheetConfigurationForecastUploadRules: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_SHEET_CONFIGURATION_FORECAST_UPLOAD_RULES_PREFIX, defaults.NetworkArray, SheetConfigurationForecastUploadToSheetRule, 'SheetConfigurationForecastUploadToSheetRule'),
        sheetExcelConfigurations: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_SHEET_EXCEL_CONFIGURATIONS_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@sheetExcelConfigurationIndex': pipeReducers(
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_SHEET_EXCEL_CONFIGURATION_PREFIX),
                        networkEntityAddReducer(NetworkActionPrefix.ADD_SHEET_EXCEL_CONFIGURATION_PREFIX, null, (state, action) => action.sheetExcelConfiguration),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_SHEET_EXCEL_CONFIGURATION_PREFIX, null, (state, action) => action.sheetExcelConfiguration),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_SHEET_EXCEL_CONFIGURATION_PREFIX),
                        networkActionReducer(NetworkActionPrefix.STORE_EXCEL_FILE_AND_SET_URI_TO_CONFIGURATION_PREFIX),
                        combineReducers({
                            Custom: combineReducers({
                                isAuthenticated: networkObjectLoadReducer(NetworkActionPrefix.CHECK_EXCEL_AUTHENTICATION_PREFIX, defaults.SheetExcelConfiguration.Custom.isAuthenticated)
                            })
                        }),
                        (state, action) => {
                            switch (action.type) {
                                case ActionType.STORE_EXCEL_FILE_AND_SET_URI_TO_CONFIGURATION_SUCCESS:
                                    return deepAssign(state, { TemplateFileBlobStorageURI: action.response });

                                case ActionType.LOAD_SHEET_EXCEL_CONFIGURATION_SUCCESS:
                                    return deepAssign(state, { Custom: { isAuthenticated: defaults.SheetExcelConfiguration.Custom.isAuthenticated } });

                                default:
                                    return state;
                            }
                        }
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.CREATE_EXCEL_CONFIGURATION_FROM_SHARE_URL_SUCCESS:
                        const data = state.Data.copy();
                        data.push(action.response);

                        return deepAssign(state, { Data: data.sortBy(({ Name }) => Name) });

                    case ActionType.DELETE_SHEET_FORECAST_FILE_SUCCESS:
                    case ActionType.DELETE_SHEET_FORECAST_SUCCESS:
                        return defaults.NetworkArray;

                    default:
                        return state;
                }
            }
        ),
        sheetGoogleConfigurations: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_SHEET_GOOGLE_CONFIGURATIONS_PREFIX, defaults.NetworkArray),
            networkActionReducer(NetworkActionPrefix.CLONE_SHEET_GOOGLE_CONFIGURATION_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@sheetGoogleConfigurationIndex': pipeReducers(
                        networkEntityAddReducer(NetworkActionPrefix.ADD_SHEET_GOOGLE_CONFIGURATION_PREFIX, null, (state, action) => action.sheetGoogleConfiguration),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_SHEET_GOOGLE_CONFIGURATION_PREFIX, null, (state, action) => action.sheetGoogleConfiguration),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_SHEET_GOOGLE_CONFIGURATION_PREFIX),
                        combineReducers({
                            Custom: combineReducers({
                                isAuthenticated: networkObjectLoadReducer(NetworkActionPrefix.CHECK_GOOGLE_AUTHENTICATION_PREFIX, defaults.SheetGoogleConfiguration.Custom.isAuthenticated)
                            })
                        })
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.CREATE_GOOGLE_SPREADSHEET_FROM_INDICATORS_SUCCESS:
                    case ActionType.CREATE_GOOGLE_SPREADSHEET_FROM_FINANCIAL_PROFIT_AND_LOSS_INDICATORS_SUCCESS:
                        return deepAssign(state, { Data: [...state.Data, action.response].sortBy(({ SpreadsheetName }) => SpreadsheetName) });

                    case ActionType.DELETE_GOOGLE_OAUTH_USER_SUCCESS:
                        return deepAssign(state, {
                            Data: state.Data.map((configuration) => {
                                if (configuration.GoogleOAuthUserID === action.googleOAuthUserID) {
                                    return deepAssign(configuration, {
                                        Custom: {
                                            isAuthenticated: {
                                                Data: false
                                            }
                                        },
                                        GoogleOAuthUserID: null
                                    });
                                }

                                return configuration;
                            })
                        });

                    case ActionType.CLONE_SHEET_GOOGLE_CONFIGURATION_SUCCESS:
                    case ActionType.CLONE_SHEET_GOOGLE_CONFIGURATION_FAIL:
                    case ActionType.ADD_SHEET_FORECAST_FILE_SUCCESS:
                    case ActionType.DELETE_SHEET_FORECAST_FILE_SUCCESS:
                    case ActionType.DELETE_SHEET_FORECAST_SUCCESS:
                        return defaults.NetworkArray;

                    default:
                        return state;
                }
            }
        )
    });

export default sheets;
